import React, { ComponentProps } from 'react';
import { Field } from 'react-form';
import { ErrorMessage } from 'v1/components/shared';
import { getValidatorMessages } from 'v1/helpers/validation';
import { TernaryToggle } from 'v5/design-system/TernaryToggle';

type TernaryToggleFieldProps = Omit<
  ComponentProps<typeof TernaryToggle>,
  'value' | 'onChange'
> & {
  /**
   * Field name for react-form
   */
  field: string;
  /**
   * Validation rules as comma-separated string (e.g., "required")
   * For ternary toggle, 'required' means value must not be null/undefined.
   * Both true and false are valid values.
   */
  validate?: string;
  /**
   * Callback fired when value changes
   * @param field - The field name
   * @param value - The new value (true, false, or null)
   */
  onChange?: (field: string, value: boolean | null) => void;
};

/**
 * Wrapper component for TernaryToggle that integrates with react-form.
 * Handles validation, error display, and form state management.
 *
 * @example
 * ```tsx
 * // Required field - user must choose true or false
 * <TernaryToggleField
 *   field="acceptTerms"
 *   validate="required"
 * />
 *
 * // Optional field - all values (true/false/null) are valid
 * <TernaryToggleField
 *   field="newsletter"
 * />
 * ```
 */
const TernaryToggleField: React.FC<TernaryToggleFieldProps> = ({
  field,
  validate: validateProp,
  onChange,
  ...ternaryToggleProps
}) => {
  const handleValidate = (value: boolean | null | undefined): string | null => {
    // For TernaryToggle: false is valid, only null/undefined triggers error
    if (validateProp === 'required') {
      if (value === null || value === undefined) {
        const VALIDATOR_MESSAGES = getValidatorMessages();
        return VALIDATOR_MESSAGES.required;
      }
    }
    return null;
  };

  return (
    <Field field={field} validate={handleValidate}>
      {fieldApi => (
        <div className="tailwind-root flex flex-col gap-2">
          <TernaryToggle
            value={fieldApi.value ?? null}
            onChange={value => {
              fieldApi.setValue(value);
              if (onChange) {
                onChange(field, value);
              }
            }}
            {...ternaryToggleProps}
          />
          {fieldApi.error && (
            <div>
              <ErrorMessage className="" appearance="text">
                {fieldApi.error}
              </ErrorMessage>
            </div>
          )}
        </div>
      )}
    </Field>
  );
};

export default TernaryToggleField;
